import experiment from 'experiment';
import { isOnWixStudio } from '@wix/santa-editor-utils';
import * as coreBi from '#packages/coreBi';
import { notifications, userPreferences } from '#packages/stateManagement';
import type { EditorAPI } from '#packages/editorAPI';
import type { PagesData } from 'types/documentServices';
import {
  AUTH_VALUES,
  CUSTOM_LOGIN_APP_DEF_ID,
  CUSTOM_REGISTRATION_APP_DEF_ID,
  MEMBER_AUTH_APP_DEF_ID,
  type FormType,
} from '../../../utils/customSignup';
import {
  MEMBER_AUTH_SUCCESS_MODAL_PANEL_NAME,
  type MemberAuthSuccessModalProps,
} from './memberAuthSuccessModal';
import {
  MEMBER_AUTH_CUSTOM_FORM_WARNING_MODAL_PANEL_NAME,
  type MemberAuthCustomFormWarningModalProps,
} from './memberAuthCustomFormWarningModal';
import {
  MEMBER_AUTH_INSTALL_ERROR_MODAL_PANEL_NAME,
  type MemberAuthInstallErrorModalProps,
} from './memberAuthInstallErrorModal';

const BI_ORIGIN = 'memberAuthMigration';
const LEAVE_OTHER_PANELS_OPEN = true;
const HAD_MANAGED_CUSTOM_FORMS_PREF =
  'memberAuthMigration.hadManagedCustomFormsBeforeInstall';

// The banner is gated by a dedicated per-editor spec so its exposure never
// piggybacks on the install spec: Studio has its own banner FT, decoupled from
// the V2 install rollout. Enabling the banner in either editor is a Petri flip.
const MIGRATION_BANNER_EXPERIMENT_CLASSIC = 'se_showAuthDeprecationBanner';
const MIGRATION_BANNER_EXPERIMENT_STUDIO = 'se_showAuthDeprecationBannerStudio';
// The auth-app install is gated per editor by a dedicated spec, mirroring
// permissionsTabMapper: V2 drives Studio (aligned with members-area FTs), V3
// drives Classic. Picking the spec per editor keeps Studio enablement a Petri
// flip rather than a code change.
const IDENTITY_AUTH_INSTALL_EXPERIMENT_STUDIO =
  'specs.ident.shouldInstallIdentityAuthAppV2';
const IDENTITY_AUTH_INSTALL_EXPERIMENT_CLASSIC =
  'specs.ident.shouldInstallIdentityAuthAppV3';

export const MIGRATION_FLOWS = {
  OLD_DEFAULT_FORM: 'old default form',
  OLD_CUSTOM_FORM: 'old custom form',
  CUSTOM_FORM: 'custom form',
} as const;
export type MigrationFlow =
  (typeof MIGRATION_FLOWS)[keyof typeof MIGRATION_FLOWS];

// Invoked only after a successful install, so callers can dismiss the
// now-obsolete old signup settings panel.
type OnInstalled = () => void;

export const MIGRATION_MODAL_NAMES = {
  BANNER: 'Unlock new member screens',
  WARNING: 'add app to site',
  TOAST: 'member authentication success',
  SUCCESS: 'app is installed',
  ERROR: 'unable to add app',
} as const;
type MigrationModalName =
  (typeof MIGRATION_MODAL_NAMES)[keyof typeof MIGRATION_MODAL_NAMES];

export const MIGRATION_CTA_TYPES = {
  ADD_TO_SITE: 'add to site',
  LEARN_MORE: 'learn more',
  ADD: 'add',
  WHY: 'why',
  CANCEL: 'cancel',
  OPEN: 'open',
  TRY_AGAIN: 'try again',
  HELP: 'help',
} as const;

export const computeFlow = (
  formType: FormType,
  hasCustomForm: boolean,
): MigrationFlow => {
  if (hasCustomForm) {
    return MIGRATION_FLOWS.OLD_CUSTOM_FORM;
  }
  if (formType === AUTH_VALUES.CUSTOM) {
    return MIGRATION_FLOWS.CUSTOM_FORM;
  }
  return MIGRATION_FLOWS.OLD_DEFAULT_FORM;
};

const reportModalView = (
  editorAPI: EditorAPI,
  flow: MigrationFlow,
  modalName: MigrationModalName,
): void => {
  editorAPI.bi.event(coreBi.events.memberAuthMigration.modal_view, {
    flow,
    modal_name: modalName,
  });
};

const reportModalClick = (
  editorAPI: EditorAPI,
  flow: MigrationFlow,
  modalName: MigrationModalName,
  ctaType: string,
): void => {
  editorAPI.bi.event(coreBi.events.memberAuthMigration.modal_click, {
    flow,
    modal_name: modalName,
    cta_type: ctaType,
  });
};

export interface BannerBiContext {
  formType: FormType;
  hasCustomForm: boolean;
}

export const reportMigrationBannerView = (
  editorAPI: EditorAPI,
  { formType, hasCustomForm }: BannerBiContext,
): void => {
  reportModalView(
    editorAPI,
    computeFlow(formType, hasCustomForm),
    MIGRATION_MODAL_NAMES.BANNER,
  );
};

export const reportMigrationBannerClick = (
  editorAPI: EditorAPI,
  { formType, hasCustomForm }: BannerBiContext,
  ctaType: string,
): void => {
  reportModalClick(
    editorAPI,
    computeFlow(formType, hasCustomForm),
    MIGRATION_MODAL_NAMES.BANNER,
    ctaType,
  );
};

export const isMigrationBannerEnabledForFormType = (
  formType: FormType,
): boolean => {
  if (formType !== AUTH_VALUES.DEFAULT && formType !== AUTH_VALUES.CUSTOM) {
    return false;
  }
  const isStudio = isOnWixStudio();
  const identityAuthInstallExperiment = isStudio
    ? IDENTITY_AUTH_INSTALL_EXPERIMENT_STUDIO
    : IDENTITY_AUTH_INSTALL_EXPERIMENT_CLASSIC;
  const migrationBannerExperiment = isStudio
    ? MIGRATION_BANNER_EXPERIMENT_STUDIO
    : MIGRATION_BANNER_EXPERIMENT_CLASSIC;
  return (
    experiment.isOpen(identityAuthInstallExperiment) &&
    experiment.isOpen(migrationBannerExperiment)
  );
};

const navigateToMemberAuthPagesPanel = (editorAPI: EditorAPI): void => {
  editorAPI.panelHelpers.openPagesPanel({
    selectedTabId: MEMBER_AUTH_APP_DEF_ID,
    origin: BI_ORIGIN,
  });
};

const openSuccessModal = (editorAPI: EditorAPI, flow: MigrationFlow): void => {
  const closeModal = () =>
    editorAPI.panelManager.closePanelByName(
      MEMBER_AUTH_SUCCESS_MODAL_PANEL_NAME,
    );

  const props: MemberAuthSuccessModalProps = {
    onClose: () => {
      reportModalClick(
        editorAPI,
        flow,
        MIGRATION_MODAL_NAMES.SUCCESS,
        MIGRATION_CTA_TYPES.CANCEL,
      );
      closeModal();
    },
    onOpenMemberAuth: () => {
      reportModalClick(
        editorAPI,
        flow,
        MIGRATION_MODAL_NAMES.SUCCESS,
        MIGRATION_CTA_TYPES.OPEN,
      );
      closeModal();
      navigateToMemberAuthPagesPanel(editorAPI);
    },
  };

  reportModalView(editorAPI, flow, MIGRATION_MODAL_NAMES.SUCCESS);
  editorAPI.panelManager.openPanel(
    MEMBER_AUTH_SUCCESS_MODAL_PANEL_NAME,
    props,
    LEAVE_OTHER_PANELS_OPEN,
  );
};

const openHelpCenter = (editorAPI: EditorAPI, helpId: string): void => {
  editorAPI.panelManager.openHelpCenter(helpId, null, {
    origin: BI_ORIGIN,
  });
};

const openInstallErrorModal = (
  editorAPI: EditorAPI,
  flow: MigrationFlow,
  onInstalled?: OnInstalled,
): void => {
  const closeModal = () =>
    editorAPI.panelManager.closePanelByName(
      MEMBER_AUTH_INSTALL_ERROR_MODAL_PANEL_NAME,
    );

  const props: MemberAuthInstallErrorModalProps = {
    onClose: () => {
      reportModalClick(
        editorAPI,
        flow,
        MIGRATION_MODAL_NAMES.ERROR,
        MIGRATION_CTA_TYPES.CANCEL,
      );
      closeModal();
    },
    onCancel: () => {
      reportModalClick(
        editorAPI,
        flow,
        MIGRATION_MODAL_NAMES.ERROR,
        MIGRATION_CTA_TYPES.CANCEL,
      );
      closeModal();
    },
    onTryAgain: () => {
      reportModalClick(
        editorAPI,
        flow,
        MIGRATION_MODAL_NAMES.ERROR,
        MIGRATION_CTA_TYPES.TRY_AGAIN,
      );
      closeModal();
      void performInstall(editorAPI, flow, onInstalled);
    },
  };

  reportModalView(editorAPI, flow, MIGRATION_MODAL_NAMES.ERROR);
  editorAPI.panelManager.openPanel(
    MEMBER_AUTH_INSTALL_ERROR_MODAL_PANEL_NAME,
    props,
    LEAVE_OTHER_PANELS_OPEN,
  );
};

const performInstall = async (
  editorAPI: EditorAPI,
  flow: MigrationFlow,
  onInstalled?: OnInstalled,
): Promise<void> => {
  const hadManagedCustomForms = hasAnyManagedCustomForm(editorAPI);
  try {
    await editorAPI.tpa.installAppIfNeeded(MEMBER_AUTH_APP_DEF_ID, {
      biData: { origin: BI_ORIGIN },
    });
  } catch (error) {
    console.error(
      '[memberAuthMigration] failed to install Member Authentication app',
      error,
    );
    openInstallErrorModal(editorAPI, flow, onInstalled);
    return;
  }
  if (hadManagedCustomForms) {
    editorAPI.store.dispatch(
      userPreferences.actions.setSiteUserPreferences(
        HAD_MANAGED_CUSTOM_FORMS_PREF,
        true,
      ),
    );
  }
  // Classic registers the app-removed hook at editor init (via onPreviewReady),
  // but that path doesn't run in Studio — so the "old forms restored" toast
  // never fires there. Register it here on install (Studio only, to avoid a
  // duplicate registration in Classic) so removing the app surfaces the toast.
  if (isOnWixStudio()) {
    registerMemberAuthMigrationHooks(editorAPI);
  }
  editorAPI.pages.popupPages.close();
  reportModalView(editorAPI, flow, MIGRATION_MODAL_NAMES.TOAST);
  openSuccessModal(editorAPI, flow);
  // Dismiss the now-obsolete old signup settings panel. In Classic this
  // happens implicitly once the signup tab is removed from the pages panel;
  // Studio's pages panel is host-driven and won't, so close it explicitly.
  onInstalled?.();
};

const openCustomFormWarningModal = (
  editorAPI: EditorAPI,
  flow: MigrationFlow,
  helpId: string,
  onInstalled?: OnInstalled,
): void => {
  const closeModal = () =>
    editorAPI.panelManager.closePanelByName(
      MEMBER_AUTH_CUSTOM_FORM_WARNING_MODAL_PANEL_NAME,
    );

  const props: MemberAuthCustomFormWarningModalProps = {
    onClose: () => {
      reportModalClick(
        editorAPI,
        flow,
        MIGRATION_MODAL_NAMES.WARNING,
        MIGRATION_CTA_TYPES.CANCEL,
      );
      closeModal();
    },
    onCancel: () => {
      reportModalClick(
        editorAPI,
        flow,
        MIGRATION_MODAL_NAMES.WARNING,
        MIGRATION_CTA_TYPES.CANCEL,
      );
      closeModal();
    },
    onConfirm: () => {
      reportModalClick(
        editorAPI,
        flow,
        MIGRATION_MODAL_NAMES.WARNING,
        MIGRATION_CTA_TYPES.ADD,
      );
      closeModal();
      void performInstall(editorAPI, flow, onInstalled);
    },
    onLearnMore: () => {
      reportModalClick(
        editorAPI,
        flow,
        MIGRATION_MODAL_NAMES.WARNING,
        MIGRATION_CTA_TYPES.WHY,
      );
      openHelpCenter(editorAPI, helpId);
    },
  };

  reportModalView(editorAPI, flow, MIGRATION_MODAL_NAMES.WARNING);
  editorAPI.panelManager.openPanel(
    MEMBER_AUTH_CUSTOM_FORM_WARNING_MODAL_PANEL_NAME,
    props,
    LEAVE_OTHER_PANELS_OPEN,
  );
};

export const installMemberAuthApp = async (
  editorAPI: EditorAPI,
  options: {
    formType: FormType;
    hasCustomForm: boolean;
    helpId: string;
    onInstalled?: OnInstalled;
  },
): Promise<void> => {
  const flow = computeFlow(options.formType, options.hasCustomForm);
  if (options.hasCustomForm) {
    openCustomFormWarningModal(
      editorAPI,
      flow,
      options.helpId,
      options.onInstalled,
    );
    return;
  }
  await performInstall(editorAPI, flow, options.onInstalled);
};

const hasAnyManagedCustomForm = (editorAPI: EditorAPI): boolean => {
  const popups: PagesData[] = editorAPI.pages.popupPages.getDataList();
  return popups.some(
    (popup) =>
      popup.managingAppDefId === CUSTOM_REGISTRATION_APP_DEF_ID ||
      popup.managingAppDefId === CUSTOM_LOGIN_APP_DEF_ID,
  );
};

export const onMemberAuthAppDeleted = (editorAPI: EditorAPI): void => {
  // Member Auth archives managed popups on install, so the live state is empty by uninstall — the persisted flag is the only durable signal across sessions.
  const hadManagedCustomForms =
    userPreferences.selectors.getSiteUserPreferences(
      HAD_MANAGED_CUSTOM_FORMS_PREF,
    )(editorAPI.store.getState());
  const hasManagedNow = hasAnyManagedCustomForm(editorAPI);

  if (!hadManagedCustomForms && !hasManagedNow) {
    return;
  }

  editorAPI.store.dispatch(
    notifications.actions.showUserActionNotification({
      message: 'member_authentication_app_old_forms_restored_toast',
      title: 'member_authentication_app_old_forms_restored_toast',
      type: 'info',
      shouldTranslate: true,
    }),
  );

  if (hadManagedCustomForms) {
    editorAPI.store.dispatch(
      userPreferences.actions.setSiteUserPreferences(
        HAD_MANAGED_CUSTOM_FORMS_PREF,
        false,
      ),
    );
  }
};

const editorAPIsWithAppRemovedHook = new WeakSet<EditorAPI>();

export const registerMemberAuthMigrationHooks = (
  editorAPI: EditorAPI,
): void => {
  // Idempotent per editor session: this registers a single global app-removed
  // listener. It can be called from more than one place (Classic init, and
  // again per-install in Studio), so guard against duplicate listeners that
  // would fire the restored toast multiple times on a single uninstall.
  if (editorAPIsWithAppRemovedHook.has(editorAPI)) {
    return;
  }
  editorAPIsWithAppRemovedHook.add(editorAPI);
  editorAPI.documentServices.platform.registerToAppRemoved(
    (appDefinitionId: string) => {
      if (appDefinitionId !== MEMBER_AUTH_APP_DEF_ID) {
        return;
      }
      onMemberAuthAppDeleted(editorAPI);
    },
  );
};
